import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Check, X, AlertTriangle } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const isPlaceholder = (email) => !email || email.toLowerCase().endsWith('@betohalics.app');

export default function EmailManageCard() {
    const { user, refreshUser } = useAuth();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);

    const placeholder = isPlaceholder(user?.email);

    const start = () => { setValue(placeholder ? '' : (user?.email || '')); setEditing(true); };

    const save = async () => {
        const email = value.trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Enter a valid email'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/auth/update-email`, { email }, { withCredentials: true });
            toast.success('Email saved — you can now reset your password by email');
            await refreshUser?.();
            setEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save email');
        } finally { setSaving(false); }
    };

    if (editing) {
        return (
            <div className="mt-2 flex items-center gap-2" data-testid="email-manage-edit">
                <Input type="email" inputMode="email" value={value} autoFocus
                    onChange={(e) => setValue(e.target.value)} placeholder="you@email.com"
                    className="h-8 bg-[#0a0a0a] border-white/10 text-white text-sm"
                    data-testid="email-manage-input" />
                <Button size="sm" onClick={save} disabled={saving}
                    className="h-8 px-2 bg-[#39FF14] text-black hover:bg-[#39FF14]/80" data-testid="email-manage-save">
                    <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}
                    className="h-8 px-2 border-white/10 text-[#A0AAB2]" data-testid="email-manage-cancel">
                    <X className="w-4 h-4" />
                </Button>
            </div>
        );
    }

    if (placeholder) {
        return (
            <div className="mt-2 rounded-lg bg-[#FFB020]/10 border border-[#FFB020]/40 p-2.5" data-testid="email-manage-prompt">
                <p className="text-[11px] text-[#FFB020] font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> No real email on file
                </p>
                <p className="text-[11px] text-[#A0AAB2] mt-0.5">Add your email so you can reset a lost password yourself.</p>
                <Button size="sm" onClick={start}
                    className="mt-2 h-7 bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold text-xs"
                    data-testid="email-manage-add">
                    Add email
                </Button>
            </div>
        );
    }

    return (
        <button type="button" onClick={start}
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#A0AAB2] hover:text-[#FFD700]"
            data-testid="email-manage-change">
            <Pencil className="w-3 h-3" /> Change email
        </button>
    );
}
